import { ArrowLeft, Check, Image, Save } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DatabaseTooltipMethods from '../../data/database.tooltips.methods';
import { inspectionDB } from '../../data/database.structure';
import { TooltipModel } from '../../data/types';
import { MdHelpers } from '../../helpers/mdHelpers';
import { marked } from 'marked';
import { FileHelpers } from '../../helpers/fileHelpers';

interface TooltipEditorProps {
  tooltipKey: string;
  isOpen: boolean;
  onClose: () => void;
}

export const TooltipEditor: React.FC<TooltipEditorProps> = ({
  tooltipKey,
  onClose,
}) => {
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const imageUploadRef = useRef<HTMLInputElement | null>(null);

  const tooltipDbMethods = useMemo(
    () => new DatabaseTooltipMethods(inspectionDB),
    [],
  );
  const [tooltipModel, setTooltipModel] = useState<TooltipModel | null>(null);
  const [heading, setHeading] = useState<string>('');
  const [markdownText, setMarkdownText] = useState<string>('');
  const [htmlText, setHtmlText] = useState<string>('');
  const [isSaveComplete, setIsSaveComplete] = useState<boolean>(false);

  useEffect(() => {
    const retrieveTooltip = async () => {
      if (tooltipModel === null || tooltipModel.key !== tooltipKey) {
        const newTooltipModel = await tooltipDbMethods.getTooltip(tooltipKey);
        setTooltipModel(newTooltipModel);
        setMarkdownText(newTooltipModel.markdownBody);
        setHeading(newTooltipModel.heading);
      }
    };
    retrieveTooltip();
  }, [tooltipDbMethods, tooltipKey, tooltipModel]);

  useEffect(() => {
    const parseMarkdown = async () => {
      setHtmlText(await marked(markdownText));
    };
    parseMarkdown();
  }, [markdownText]);

  const displayHtml = useMemo(
    () =>
      MdHelpers.getHtmlElementForHtmlString(
        htmlText,
        MdHelpers.defaultMarkdownStyles,
      ),
    [htmlText],
  );

  const save = useCallback(() => {
    tooltipDbMethods
      .upsertTooltip(tooltipKey, heading, markdownText)
      .then(() => {
        setIsSaveComplete(true);
        setTimeout(() => setIsSaveComplete(false), 1500);
      });
  }, [heading, markdownText, tooltipDbMethods, tooltipKey]);

  const uploadImages = useCallback(
    (files: FileList | null) => {
      const uploadImagesAsync = async () => {
        if (files === null) {
          return;
        }

        const dataUrls = await FileHelpers.getFilesAsDataUrl(files);
        const markdownUrls = dataUrls.map((url) => `![](${url})`).join('');
        const insertPosition = textAreaRef.current?.selectionEnd ?? 0;
        const leftText = markdownText.slice(0, insertPosition);
        const rightText = markdownText.slice(insertPosition);
        setMarkdownText(`${leftText}${markdownUrls}${rightText}`);
      };
      uploadImagesAsync();
    },
    [markdownText],
  );

  return (
    <div className="size-full bg-white">
      <div className="flex flex-row items-center px-3 py-2">
        <button onClick={onClose}>
          <ArrowLeft size={24} />
        </button>
        <div className="px-6 text-lg font-bold">Markdown Editor</div>
        {isSaveComplete ? (
          <div className="ml-auto px-3">
            <Check />
          </div>
        ) : (
          <button className="ml-auto px-3" onClick={save}>
            <Save />
          </button>
        )}
        <input
          className="hidden"
          ref={imageUploadRef}
          type="file"
          onChange={(event) => uploadImages(event.target.files)}
        />
        <Image
          className="cursor-pointer"
          onClick={() => imageUploadRef.current?.click()}
        />
      </div>
      <div className="flex h-full flex-row">
        <div className="flex-1 border-r border-solid border-black">
          <div className="px-3 text-sm font-normal text-inkMuted">
            ID: {tooltipKey}
          </div>
          <input
            className="w-full border border-solid border-black p-3 text-base font-normal text-inkMuted"
            type="text"
            value={heading}
            onChange={(event) => setHeading(event.target.value)}
          />
          <textarea
            ref={textAreaRef}
            className="size-full px-3 text-base font-normal text-inkMuted"
            value={markdownText}
            onChange={(event) => setMarkdownText(event.target.value)}
          />
        </div>
        <div className="flex-1 px-3">
          <div dangerouslySetInnerHTML={{ __html: displayHtml }} />
        </div>
      </div>
    </div>
  );
};
